/**
 * Innu Shell postMessage bridge.
 *
 * The Shell (https://innu.app) embeds this app in an iframe. Protocol
 * (innu-core/shell/src/federation/AppLoader.tsx):
 *   child → Shell   { type: 'innu:route', path }   on every inner route change
 *   child → Shell   { type: 'innu:exit' }          "back to Innu" from the brand link
 *   Shell → child   { type: 'innu:navigate', path } the Shell wants us on a route
 *
 * Every message is scoped to the Shell origin — never `*` — and inbound
 * messages from any other origin are ignored.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { consumeLaunchPath } from './auth';

const SHELL_URL = import.meta.env.VITE_INNU_SHELL_URL ?? 'https://innu.app';

export type ShellOutbound =
  | { type: 'innu:route'; path: string }
  | { type: 'innu:exit' };

export interface ShellNavigate {
  type: 'innu:navigate';
  path: string;
}

/** Minimal window surface so tests can pass a fake instead of the real one. */
export interface BridgeWindow {
  parent: Pick<Window, 'postMessage'> | null;
}

export function shellOrigin(): string {
  try {
    return new URL(SHELL_URL).origin;
  } catch {
    return 'https://innu.app';
  }
}

/** True when this document is inside another frame (the Innu Shell). */
export function isEmbedded(win: BridgeWindow = window): boolean {
  try {
    return win.parent !== null && win.parent !== (win as unknown);
  } catch {
    // Cross-origin parent access can throw in some browsers — that still
    // means "there is a parent".
    return true;
  }
}

function post(msg: ShellOutbound, win: BridgeWindow): boolean {
  if (!isEmbedded(win) || !win.parent) return false;
  win.parent.postMessage(msg, shellOrigin());
  return true;
}

/** Tell the Shell which inner route we are on. Returns false when not embedded. */
export function postRoute(path: string, win: BridgeWindow = window): boolean {
  return post({ type: 'innu:route', path }, win);
}

/** Ask the Shell to close this app and go back to its home. */
export function postExit(win: BridgeWindow = window): boolean {
  return post({ type: 'innu:exit' }, win);
}

export function isShellNavigate(data: unknown): data is ShellNavigate {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return d.type === 'innu:navigate' && typeof d.path === 'string' && d.path.startsWith('/');
}

/**
 * Keeps the Shell and the router in sync:
 *  - navigates to the `path=` the Shell put in the launch hash (once),
 *  - posts `innu:route` on every location change,
 *  - follows `innu:navigate` messages that come from the Shell origin.
 */
export function useShellRouteSync(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const launchPath = consumeLaunchPath();
    if (launchPath) navigate(launchPath, { replace: true });
  }, [navigate]);

  useEffect(() => {
    postRoute(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isEmbedded()) return;
    const origin = shellOrigin();
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== origin) return;
      if (isShellNavigate(event.data)) navigate(event.data.path);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);
}
